import { DialogComponent } from "@theme/dialog";

/**
 * A custom drawer component that is used site wide. This is a wrapper for the dialog component
 * and any data/markup can be passed to it to render a view.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */

class DrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("click", this.#handleDocumentClick);

    // closes from inside the dialog content
    this.addEventListener("click", this.#handleInternalCloseClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("click", this.#handleDocumentClick);
    this.removeEventListener("click", this.#handleInternalCloseClick);
  }

  #handleDocumentClick = (event) => {
    const btn = event.target.closest(`[data-drawer-open="drawer"]`);

    if (!btn) return;

    // Named drawers (with id) are opened via their own scoped triggers,
    // not the generic data-drawer-open mechanism.
    if (this.id) return;

    this.openFromTrigger(btn);
  };

  // Any element inside the drawer with data-drawer-close will close it
  #handleInternalCloseClick = (event) => {
    const closeEl = event.target.closest("[data-drawer-close]");
    if (!closeEl) return;

    event.preventDefault();
    this.close();
  };

  openFromTrigger(trigger) {
    const { dialog } = this.refs;
    if (!dialog) return;

    const view = trigger?.getAttribute("data-drawer-view");
    const title = trigger?.getAttribute("data-drawer-title");

    if (title) {
      const titleEl = dialog.querySelector("[data-drawer-title]");
      if (titleEl) titleEl.innerHTML = title;
    }

    const tabIndex = trigger?.getAttribute("data-tab-index");
    if (tabIndex != null && view) {
      const source = document.querySelector(`[data-drawer-view="${CSS.escape(view)}"]:not([data-drawer-open])`);
      source?.setAttribute("data-active-tab", tabIndex);
    }

    if (view) {
      this.#renderView(dialog, view);
    }

    this.showDialog();
  }

  open() {
    this.showDialog();
  }

  close() {
    const mount = this.refs.dialog?.querySelector("[data-drawer-content]");
    const content = mount?.firstElementChild;
    if (content?.dataset.drawerView) {
      content.hidden = true;
      document.body.appendChild(content); // or back to its original parent
    }
    this.closeDialog();
  }

  #renderView(dialog, view) {
    const mount = dialog.querySelector("[data-drawer-content]");
    if (!mount) return;

    const source = document.querySelector(`[data-drawer-view="${CSS.escape(view)}"]:not([data-drawer-open])`);
    if (!source) {
      mount.innerHTML = `<div class="py-6 text-sm">Missing drawer view: ${view}</div>`;
      return;
    }

    source.hidden = false;
    mount.replaceChildren(source);
  }
}

if (!customElements.get("drawer-component")) {
  customElements.define("drawer-component", DrawerComponent);
}
